import { FC } from "react";
import { Box, Typography } from "@mui/material";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { makeSxStyles } from "ui";
import { Nullable } from "service";
import { SortOptions } from "@/shared/components/table/GenericTable";
import { TEAM_MEMBERS_COLUMNS } from "../constants/team-members-columns";

export interface TeamMembersListHeaderProps {
  sortOptions: Nullable<SortOptions>;
  onSortChange: (sortOptions: Nullable<SortOptions>) => void;
}

const TeamMembersListHeader: FC<TeamMembersListHeaderProps> = ({
  sortOptions,
  onSortChange,
}) => {
  const styles = useStyles();

  const onSort = (key: string) => {
    if (!sortOptions || sortOptions.sortBy !== key) {
      onSortChange({ sortBy: key, direction: "asc" });
      return;
    }
    if (sortOptions.direction === "asc") {
      onSortChange({ sortBy: key, direction: "desc" });
      return;
    }
    onSortChange(null);
  };

  return (
    <Box sx={styles.row}>
      {TEAM_MEMBERS_COLUMNS.map((column) => (
        <Box
          key={column.key}
          sx={{
            ...styles.cell,
            width: column.size,
            ...(column.minWidth && { minWidth: column.minWidth }),
            ...(column.align === "center" && { justifyContent: "center" }),
            ...(column.align === "right" && { justifyContent: "flex-end" }),
            ...(column.sortable && { cursor: "pointer" }),
          }}
          onClick={column.sortable ? () => onSort(column.key) : undefined}
        >
          {column.label && (
            <Typography sx={styles.label} noWrap>
              {column.label}
            </Typography>
          )}
          {column.sortable && (
            <Box
              sx={{
                ...styles.sortIcons,
                opacity: sortOptions?.sortBy === column.key ? 1 : 0.5,
              }}
            >
              <ArrowDropUp
                sx={
                  sortOptions?.direction === "desc" &&
                  sortOptions?.sortBy === column.key
                    ? { ...styles.sortIcon, opacity: 0.5 }
                    : styles.sortIcon
                }
              />
              <ArrowDropDown
                sx={
                  sortOptions?.direction === "asc" &&
                  sortOptions?.sortBy === column.key
                    ? { ...styles.sortIcon, opacity: 0.5 }
                    : styles.sortIcon
                }
              />
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

const useStyles = makeSxStyles(({ palette: { grey } }) => ({
  row: {
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  cell: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "16px",
  },
  label: {
    fontFamily: "Inter",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: 0,
    color: grey[100],
  },
  sortIcons: {
    display: "flex",
    flexDirection: "column",
  },
  sortIcon: {
    margin: "-8px 0",
    color: grey[100],
  },
}));

export default TeamMembersListHeader;
